import React, { useState, useEffect } from "react";
import { FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import { Loader } from "lucide-react";
import { json } from "utils/http/client";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const MaterialCreateForm = ({ onClose }) => {
  const [materialLibrary, setMaterialLibrary] = useState([]);
  const [siteMaterials, setSiteMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const alert = useAlert();

  const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
  const siteId = localStorage.getItem("site");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const libraryData = await json(`${BASE_URL}/org/material-library/`, {
        method: "POST",
        body: { org_id: parseInt(orgId) },
      });

      const siteData = await json(
        `${BASE_URL}/inventory/get-materials-of-a-site/`,
        {
          method: "POST",
          body: { sites: siteId },
        }
      );

      setMaterialLibrary(libraryData || []);
      setSiteMaterials(siteData?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      alert.addAlert("Failed to load materials", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const availableMaterials = materialLibrary.filter(
    (libMaterial) =>
      !siteMaterials.some(
        (siteMaterial) => siteMaterial.material === libMaterial.id
      )
  );

  const handleMaterialToggle = (material) => {
    const isSelected = selectedMaterials.some((m) => m.id === material.id);

    if (isSelected) {
      setSelectedMaterials(selectedMaterials.filter((m) => m.id !== material.id));
    } else {
      setSelectedMaterials([
        ...selectedMaterials,
        { ...material, current_stock: "0" }, // Initialize with empty string for input
      ]);
    }
  };

  const handleSelectAll = () => {
    if (selectedMaterials.length === availableMaterials.length) {
      setSelectedMaterials([]);
    } else {
      setSelectedMaterials(
        availableMaterials.map((m) => ({ ...m, current_stock: "0" }))
      );
    }
  };

  const handleStockChange = (materialId, value) => {
    setSelectedMaterials(
      selectedMaterials.map((m) =>
        m.id === materialId
          ? { ...m, current_stock: value } // Store as string to allow empty input
          : m
      )
    );
  };

  const handleRemoveMaterial = (materialId) => {
    setSelectedMaterials(selectedMaterials.filter((m) => m.id !== materialId));
  };

  const handleSubmit = async () => {
    if (selectedMaterials.length === 0) {
      alert.addAlert("Please select at least one material", "error");
      return;
    }

    const hasInvalidStock = selectedMaterials.some(
      (m) => !m.current_stock || parseFloat(m.current_stock) < 0
    );

    if (hasInvalidStock) {
      alert.addAlert("Please enter valid stock quantities for all materials", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        organization_id: parseInt(orgId),
        materials: selectedMaterials.map((m) => ({
          sites: parseInt(siteId),
          material: m.id,
          current_stock: parseFloat(m.current_stock),
        })),
      };

      const response = await json(
        `${BASE_URL}/inventory/create-multiple-materials/`,
        {
          method: "POST",
          body: payload,
        }
      );

      if (response.status) {
        alert.addAlert(response.message || "Materials added successfully", "success");
        onClose();
      } else {
        throw new Error(response.message || "Failed to add materials");
      }
    } catch (error) {
      console.error("Error creating materials:", error);
      alert.addAlert(error.message || "Failed to add materials", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const allSelected =
    availableMaterials.length > 0 &&
    selectedMaterials.length === availableMaterials.length;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-stretch justify-end z-50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl h-full flex flex-col rounded-l-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-800">Add New Materials</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 pb-24">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-10 h-10 animate-spin text-purple-500" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Available Materials Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-700">
                    Available Materials from Library
                  </label>
                  {availableMaterials.length > 0 && (
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      {allSelected ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>

                {availableMaterials.length === 0 ? (
                  <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <p className="text-gray-500">
                      All materials from library are already added to this site
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                    {availableMaterials.map((material) => {
                      const isSelected = selectedMaterials.some(
                        (m) => m.id === material.id
                      );
                      const selectedMaterial = selectedMaterials.find(
                        (m) => m.id === material.id
                      );
                      return (
                        <div
                          key={material.id}
                          className={`flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                            isSelected ? "bg-purple-50" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleMaterialToggle(material)}
                            className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded"
                            style={{ accentColor: "#3B0069" }}
                          />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800">
                              {material.material_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {material.subcategory} | {material.brand_name} |{" "}
                              {material.specification}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Quantity"
                                value={selectedMaterial?.current_stock || ""}
                                onChange={(e) =>
                                  handleStockChange(material.id, e.target.value)
                                }
                                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              />
                              <span className="text-sm text-gray-600 w-16">
                                {material.uom}
                              </span>
                              <button
                                onClick={() => handleRemoveMaterial(material.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <FaTrash size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="mt-2 text-sm text-gray-600">
                  {selectedMaterials.length} material(s) selected
                </p>
              </div>

              {/* Info Message */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Select materials from your organization's
                  library by checking the boxes. Enter the stock quantity for each
                  selected material. Only materials that haven't been added to this
                  site are available for selection.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-3 border-t border-gray-200 bg-white shrink-0">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 h-9 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedMaterials.length === 0}
            className={`px-4 h-9 text-sm text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium ${
              isSubmitting || selectedMaterials.length === 0
                ? "bg-gray-300"
                : "bg-sm-primary hover:bg-sm-hover"
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FaPlus size={14} />
                Add {selectedMaterials.length} Material(s)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialCreateForm;
